/**
 * Low-profile user menu for the global top bar (left-aligned, next to the
 * workspace switcher on the right). Shows just the user's name + a small
 * chevron; the dropdown reveals the email and a Logout action.
 *
 * Mirrors WorkspaceSwitcher's ``topbar`` variant styling so the two top-bar
 * controls read as a set. Uses PortalMenu (never clipped by overflow ancestors).
 */
import React, { useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';

export default function UserMenu({ user, onLogout }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const name = user.name || user.username || 'User';
  const email = user.email || user.username || '';

  return (
    <div className="relative">
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="flex max-w-[220px] items-center gap-1 rounded px-1.5 py-0.5 text-left text-[11px] text-slate-600 transition-colors hover:bg-slate-200/60 hover:text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400/40"
        aria-expanded={open}
        aria-haspopup="menu"
        title="Account"
      >
        <span className="truncate font-medium text-slate-700">{name}</span>
        <svg
          className={`h-3 w-3 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <PortalMenu anchorRef={btnRef} open={open} onClose={() => setOpen(false)}
        align="bottom-left" width={240}>
        <div className="px-3 py-2.5 border-b border-gray-100">
          <div className="truncate text-sm font-medium text-gray-800">{name}</div>
          {email ? <div className="mt-0.5 truncate text-xs text-gray-500">{email}</div> : null}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); onLogout?.(); }}
          data-testid="user-menu-logout"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" aria-hidden /> Logout
        </button>
      </PortalMenu>
    </div>
  );
}
